import { DailyProgramCta } from '@go-shared/types/dailyProgram';
import {
  BubbleButton,
  BubbleFormGroup,
  BubbleHintInput,
  BubbleInput,
} from '@go-client/components/BubbleUI';
import React from 'react';
import { FormikProps } from 'formik';
import { fme } from '@go-shared/lib/utils';

interface CallToActionsPropsBase {
  readOnly?: boolean;
  hints?: Array<{ label: string; value: string }>;
}

type CallToActionsLegacyProps = CallToActionsPropsBase & {
  type?: 'legacy';
  callToActions: DailyProgramCta[];
  onUpdate: (callToActions: DailyProgramCta[]) => void;
};

type CallToActionsFormikProps<T> = CallToActionsPropsBase & {
  type: 'formik';
  formik: FormikProps<T>;
};

export type CallToActionsProps<T> = CallToActionsLegacyProps | CallToActionsFormikProps<T>;

export function CallToActions<T extends { callToActions: DailyProgramCta[] }>(
  props: CallToActionsProps<T>
) {
  const { readOnly } = props;

  const callToActions =
    props.type === 'formik' ? props.formik.values.callToActions : props.callToActions;

  return (
    <fieldset
      disabled={readOnly}
      className="bg-white p-4 border rounded shadow grid grid-cols-1 gap-4 justify-items-start"
    >
      <div className="text-xs text-slate italic">
        Make sure links start with http:// or https:// if you want them to open a webpage. You can
        also create links for email or phone numbers: mailto:johndoe@example.com → opens the guest's
        email to write to johndoe@example.com. tel:123-456-7890 → opens the phone app to call
        123-456-7890.
      </div>
      {(callToActions || []).map((cta, i) => (
        <div key={i} className="w-full grid grid-cols-2 gap-4 items-start">
          <BubbleFormGroup
            label="Link"
            className="flex-grow"
            asterisk
            error={
              props.type === 'formik'
                ? fme(props.formik.errors, props.formik.touched, `callToActions[${i}].link` as any)
                : undefined
            }
          >
            <BubbleHintInput
              block
              name={`callToActions[${i}].link`}
              value={cta.link}
              onValue={val => {
                if (props.type === 'formik') {
                  props.formik.setFieldValue(`callToActions[${i}].link`, val);
                } else {
                  const newCta = [...callToActions];
                  newCta[i] = { ...newCta[i], link: val };
                  props.onUpdate(newCta);
                }
              }}
              options={props.hints?.map(h => h.value) ?? []}
              labelExtractor={val => props.hints?.find(h => h.value === val)?.label || val}
              onBlur={props.type === 'formik' ? props.formik.handleBlur : undefined}
            />
          </BubbleFormGroup>
          <div className="flex-grow flex items-center">
            <BubbleFormGroup
              label="Label"
              className="flex-grow mr-3"
              asterisk
              error={
                props.type === 'formik'
                  ? fme(
                      props.formik.errors,
                      props.formik.touched,
                      `callToActions[${i}].label` as any
                    )
                  : undefined
              }
            >
              <BubbleInput
                type="text"
                block
                name={`callToActions[${i}].label`}
                value={cta.label}
                onValue={val => {
                  if (props.type === 'formik') {
                    props.formik.setFieldValue(`callToActions[${i}].label`, val);
                  } else {
                    const newCta = [...callToActions];
                    newCta[i] = { ...newCta[i], label: val };
                    props.onUpdate(newCta);
                  }
                }}
                onBlur={props.type === 'formik' ? props.formik.handleBlur : undefined}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Tab">
              <BubbleInput
                type="checkbox"
                name={`callToActions[${i}].target`}
                checked={cta.target === '_blank'}
                onValue={chk => {
                  const target = chk ? '_blank' : '';
                  if (props.type === 'formik') {
                    props.formik.setFieldValue(`callToActions[${i}].target`, target);
                  } else {
                    const newCta = [...callToActions];
                    newCta[i] = { ...newCta[i], target };
                    props.onUpdate(newCta);
                  }
                }}
                onBlur={props.type === 'formik' ? props.formik.handleBlur : undefined}
              />
            </BubbleFormGroup>

            {!readOnly && (
              <BubbleButton
                style={{ alignSelf: 'end' }}
                variant="crimson"
                className="-mr-3"
                inverse
                icon="trash-alt"
                onClick={() => {
                  const newVal = [...callToActions];
                  newVal.splice(i, 1);
                  if (props.type === 'formik') {
                    props.formik.setFieldValue('callToActions', newVal);
                  } else {
                    props.onUpdate(newVal);
                  }
                }}
              />
            )}
          </div>
        </div>
      ))}
      {readOnly && callToActions.length < 1 && <span className="text-smoke italic">None</span>}
      {!readOnly && (
        <BubbleButton
          inverse
          className="p-0-i"
          onClick={() => {
            const newVal = [...callToActions, { link: '', label: '', target: '_blank' }];
            if (props.type === 'formik') {
              props.formik.setFieldValue('callToActions', newVal);
            } else {
              props.onUpdate(newVal);
            }
          }}
          variant="navy"
          icon="plus"
        >
          Add Call to Action
        </BubbleButton>
      )}
    </fieldset>
  );
}
